import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { AppHeader } from "../components/app_header";
import { Container, Section, Title } from "../components/styled";
import { Season as SeasonModel } from "../model/Season";
import { useResource } from "../hooks/useResource";

const SeasonsList = styled.div`
    margin-top: 1em;
`;

const SeasonItem = styled.div`
    padding: 1em;
    margin: 0.5em 0;
    border: 1px solid #ccc;
    border-radius: 8px;
    background-color: #f9f9f9;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #f0f0f0;
        border-color: #999;
    }
`;

const SeasonLink = styled(Link)`
    text-decoration: none;
    color: inherit;
    display: block;
    
    &:hover {
        text-decoration: none;
    }
`;

const SeasonInfo = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SeasonDetails = styled.div`
    flex: 1;
`;

const SeasonId = styled.div`
    font-weight: bold;
    font-size: 1.1em;
    margin-bottom: 0.5em;
`;

const SeasonStats = styled.div`
    font-size: 0.9em;
    color: #666;
`;

const NoSeasonsMessage = styled.div`
    text-align: center;
    padding: 2em;
    color: #666;
    font-style: italic;
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 2em;
    color: #666;
`;

const ErrorMessage = styled.div`
    text-align: center;
    padding: 2em;
    color: #dc3545;
`;

export const Seasons = () => {
    const [seasons, setSeasons] = useState<SeasonModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch all seasons
    const {
        data: seasonsData,
        loading: seasonsLoading,
        error: seasonsError,
    } = useResource<SeasonModel>({
        resourceName: 'seasons',
    });

    useEffect(() => {
        if (seasonsData && Array.isArray(seasonsData)) {
            setSeasons(seasonsData);
        }
        setLoading(seasonsLoading);
        setError(seasonsError);
    }, [seasonsData, seasonsLoading, seasonsError]);

    if (loading) {
        return (
            <Container>
                <AppHeader />
                <Section>
                    <Title>Seasons</Title>
                </Section>
                <Section>
                    <LoadingMessage>Loading seasons...</LoadingMessage>
                </Section>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <AppHeader />
                <Section>
                    <Title>Seasons</Title>
                </Section>
                <Section>
                    <ErrorMessage>Error loading seasons: {error}</ErrorMessage>
                </Section>
            </Container>
        );
    }

    return (
        <Container>
            <AppHeader />
            <Section>
                <Title>Seasons</Title>
            </Section>
            <Section>
                {seasons.length > 0 ? (
                    <SeasonsList>
                        {seasons.map((season) => (
                            <SeasonItem key={season.seasonId}>
                                <SeasonLink to={`/seasons/${season.seasonId}`}>
                                    <SeasonInfo>
                                        <SeasonDetails>
                                            <SeasonId>{season.seasonName || `Season ${season.seasonId}`}</SeasonId>
                                            <SeasonStats>
                                                {season.divisions?.length || 0} divisions, {season.weeks?.length || 0} weeks
                                            </SeasonStats>
                                        </SeasonDetails>
                                    </SeasonInfo>
                                </SeasonLink>
                            </SeasonItem>
                        ))}
                    </SeasonsList>
                ) : (
                    <NoSeasonsMessage>
                        No seasons found. Create a season from a league page.
                    </NoSeasonsMessage>
                )}
            </Section>
        </Container>
    );
};